import { hashKey, keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';
import { QK_EMAIL_FILTER_COUNTS } from '@/hooks/email-cache-keys';
import { useMembershipRecheck } from '@/hooks/use-membership-recheck';
import { createInFlightCoalescer, type InFlightCoalescer } from '@/lib/coalesce-in-flight';
import { getSupabaseFunctionError, isTransientFetchError } from '@/lib/supabase-utils';

import type { Database } from '@/integrations/supabase/types';
import type { QueryClient } from '@tanstack/react-query';

/** Query key root for filter-count caches. Other modules MUST import this —
 *  duplicating the literal risks drift if the key shape ever changes.
 *  Canonical constant lives in `email-cache-keys.ts` (import-free, so
 *  `invalidateEmailCaches` can use it without a circular import). Key shape:
 *  includes the organization, viewer, account type and each account toggle set.
 *  Per-saved-view counts moved to `useSavedViewCounts` — see
 *  docs/plans/2026-05-21-fix-saved-view-counter-perf-and-instant-updates-plan.md. */
export const FILTER_COUNTS_KEY = QK_EMAIL_FILTER_COUNTS;

export type FilterCountsAccountType = 'personal' | 'organizational' | 'all';

export function filterCountsQueryKey(
  organizationId: string | undefined,
  userId: string | undefined,
  accountType: FilterCountsAccountType | undefined,
  enabledAccountIds: readonly string[] | undefined,
  enabledMessagingAccountIds?: readonly string[],
  enabledExternalMailboxIds?: readonly string[],
) {
  return [
    FILTER_COUNTS_KEY,
    organizationId,
    userId,
    accountType,
    enabledAccountIds,
    enabledMessagingAccountIds,
    enabledExternalMailboxIds,
  ] as const;
}

// Use generated type from database and map all_emails to all for backwards compatibility
type EmailFilterCountsDB = Database['public']['CompositeTypes']['email_filter_counts_result'];

/**
 * Count variants for configurable counters
 */
export interface CountVariants {
  all: number;
  unread: number;
  assigned_to_me: number;
}

/**
 * Team member count variants (all, unread only - assigned_to_me doesn't apply)
 */
export interface TeamCountVariants {
  all: number;
  unread: number;
}

/**
 * Correspondence derived_status enum values, derived directly from the
 * generated Database types so any future enum addition reaches the counts
 * map automatically.
 */
export type DerivedStatus = Database['public']['Enums']['correspondence_derived_status'];

export interface EmailFilterCounts {
  // Legacy flat counts (for backwards compatibility)
  all: number;
  inbox: number;
  sent: number;
  archived: number;
  scheduled: number;
  snoozed: number;
  unread: number;
  assigned: number;
  todo: number;
  done: number;
  drafts: number;
  approvals: number;
  bin: number;
  teamAssignmentCounts: Record<string, number>;
  // New nested counts for configurable counters
  inboxCounts: CountVariants;
  assignedCounts: CountVariants;
  draftsCounts: CountVariants;
  approvalsCounts: CountVariants;
  doneCounts: CountVariants;
  // Team member counts with variants (all, unread)
  teamCounts: Record<string, TeamCountVariants>;
  // Per-derived_status counts (sidebar Status section)
  statusCounts: Partial<Record<DerivedStatus, CountVariants>>;
}

interface FilterCountsInput {
  organizationId: string;
  userId: string;
  accountType?: FilterCountsAccountType;
  enabledAccountIds?: readonly string[];
  enabledMessagingAccountIds?: readonly string[];
  enabledExternalMailboxIds?: readonly string[];
}

/**
 * Reads the session at fetch time so a trailing fetch carries a fresh token,
 * and refuses to fill one user's cache key with another user's counts after
 * an account switch mid-flight.
 */
async function resolveSessionFor(userId: string, signal: AbortSignal) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('User not authenticated');
  }
  signal.throwIfAborted();
  if (session.user.id !== userId) {
    throw new Error('Session user changed');
  }
  return session;
}

function buildCounterParams(input: FilterCountsInput): URLSearchParams {
  const {
    organizationId,
    accountType,
    enabledAccountIds,
    enabledMessagingAccountIds,
    enabledExternalMailboxIds,
  } = input;
  const params = new URLSearchParams({ organizationId });
  // Add account type filter if specified and not 'all'
  if (accountType && accountType !== 'all') {
    params.set('accountType', accountType);
  }
  // Add enabled account IDs filter (sidebar account toggle)
  if (enabledAccountIds) {
    params.set('enabledAccountIds', enabledAccountIds.join(','));
  }
  // Messaging (social) accounts carry their own toggle set — an empty
  // string means "every messaging account toggled off" server-side.
  if (enabledMessagingAccountIds) {
    params.set('enabledMessagingAccountIds', enabledMessagingAccountIds.join(','));
  }
  if (enabledExternalMailboxIds) {
    params.set('enabledExternalMailboxIds', enabledExternalMailboxIds.join(','));
  }
  return params;
}

/**
 * One counter fetch: session, edge invoke, error-body read. The same signal
 * bounds all three legs (lib/coalesce-in-flight.ts owns the deadline).
 */
async function fetchFilterCounts(
  input: FilterCountsInput,
  signal: AbortSignal,
): Promise<EmailFilterCounts> {
  const session = await resolveSessionFor(input.userId, signal);
  const params = buildCounterParams(input);

  type CounterResponse = EmailFilterCountsDB & {
    team_assignment_counts: Record<string, number>;
    team_counts: Record<string, TeamCountVariants>;
    inbox_counts: CountVariants;
    assigned_counts: CountVariants;
    drafts_counts: CountVariants;
    approvals_counts: CountVariants;
    done_counts: CountVariants;
    status_counts: Partial<Record<DerivedStatus, CountVariants>>;
  };

  const response: { data: CounterResponse | null; error: unknown } =
    await supabase.functions.invoke<CounterResponse>(
      `email-metadata-counter?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        signal,
      },
    );
  signal.throwIfAborted();
  const { data, error } = response;

  if (error) {
    const errorMessage = await getSupabaseFunctionError(error);
    // The body read swallows its own abort, so an expired deadline can only be
    // seen here; it must not fall through to the log below.
    signal.throwIfAborted();
    // A transport-level failure (aborted refetch on navigation, network
    // blip) is not actionable: surface it to the query error state via the
    // throw below, but skip the console.error that would route every blip to
    // Sentry as an error.
    if (!isTransientFetchError(error)) {
      console.error('Error fetching email counts:', errorMessage);
    }
    throw new Error(errorMessage);
  }

  if (!data) {
    throw new Error('No data returned from email-metadata-counter');
  }

  // Default count variants
  const defaultCountVariants: CountVariants = { all: 0, unread: 0, assigned_to_me: 0 };

  // Map all_emails to all for backwards compatibility with existing UI components
  const mappedData: EmailFilterCounts = {
    all: data.all_emails ?? 0,
    inbox: data.inbox ?? 0,
    sent: data.sent ?? 0,
    archived: data.archived ?? 0,
    scheduled: data.scheduled ?? 0,
    snoozed: data.snoozed ?? 0,
    unread: data.unread ?? 0,
    assigned: data.assigned ?? 0,
    todo: data.todo ?? 0,
    done: data.done ?? 0,
    // Counts distinct visible correspondences with my active draft, plus
    // zero-email (call/letter) and standalone compose drafts — one row
    // per visible My Drafts list entry. Parity invariant:
    // backend/supabase/tests/05110-my-drafts-count-list-parity.test.sql
    drafts: data.drafts ?? 0,
    approvals: data.approvals ?? 0,
    bin: data.bin ?? 0,
    teamAssignmentCounts: data.team_assignment_counts ?? {},
    // New nested counts for configurable counters
    inboxCounts: data.inbox_counts ?? defaultCountVariants,
    assignedCounts: data.assigned_counts ?? defaultCountVariants,
    draftsCounts: data.drafts_counts ?? defaultCountVariants,
    approvalsCounts: data.approvals_counts ?? defaultCountVariants,
    doneCounts: data.done_counts ?? defaultCountVariants,
    // Team counts with variants
    teamCounts: data.team_counts ?? {},
    statusCounts: data.status_counts ?? {},
  };

  return mappedData;
}

/**
 * Deadline for one counter fetch (session lookup, request, error-body read).
 * Above the ~15 s at which Postgres cancels this RPC's statement in production
 * (SQLSTATE 57014 at elapsed_ms ≈ 15,1xx, docs/plans/2026-09-24-fix-counter-
 * fanout-extractor-iam-and-parked-row-monitor-flap-plan.md) plus auth and
 * serialisation, so the coalescer entry always settles even when the transport
 * never answers. The function-level `SET statement_timeout` on the RPC is not
 * that bound: it never arms for the function's own body.
 */
export const COUNTER_FETCH_TIMEOUT_MS = 20_000;

type CounterCoalescer = InFlightCoalescer<EmailFilterCounts>;

// One coalescer per QueryClient: a fresh client (tests, a replaced client) can
// never share an in-flight entry with another.
const coalescers = new WeakMap<QueryClient, CounterCoalescer>();

function coalescerFor(client: QueryClient): CounterCoalescer {
  let coalescer = coalescers.get(client);
  if (!coalescer) {
    coalescer = createInFlightCoalescer<EmailFilterCounts>();
    coalescers.set(client, coalescer);
  }
  return coalescer;
}

/**
 * Hook to fetch email counts for all filter types
 * Uses consolidated edge function for efficient counting in a single atomic operation
 * @param organizationId - The organization ID to fetch counts for
 * @param accountType - Optional filter: 'personal', 'organizational', or undefined for all accounts
 * @param enabled - Optional flag to control when the query runs (e.g., only when accounts exist)
 */
export function useEmailFilterCounts(
  organizationId?: string,
  accountType?: 'personal' | 'organizational' | 'all',
  enabled = true,
  enabledAccountIds?: string[],
  enabledMessagingAccountIds?: string[],
  enabledExternalMailboxIds?: string[],
): EmailFilterCounts {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = filterCountsQueryKey(
    organizationId,
    user?.id,
    accountType,
    enabledAccountIds,
    enabledMessagingAccountIds,
    enabledExternalMailboxIds,
  );

  const { data: counts, error } = useQuery({
    queryKey,
    enabled: enabled && !!organizationId && !!user?.id,
    // Overlapping invalidations of a warm query would each start a new request
    // while the previous RPC keeps running server-side; the coalescer bounds
    // that to one active request plus one trailing refetch per key. The
    // trailing fetch starts after every invalidation it absorbs, so the badge
    // still reflects the last mutation (docs/adr/0067).
    queryFn: async () => {
      if (!organizationId || !user?.id) {
        throw new Error('Organization ID and user ID required');
      }
      const input: FilterCountsInput = {
        organizationId,
        userId: user.id,
        accountType,
        enabledAccountIds,
        enabledMessagingAccountIds,
        enabledExternalMailboxIds,
      };
      return await coalescerFor(queryClient).run(
        hashKey(queryKey),
        async (signal) => await fetchFilterCounts(input, signal),
        {
          timeoutMs: COUNTER_FETCH_TIMEOUT_MS,
          // isActive, not the observer count: a mounted observer whose `enabled`
          // is false (the page gates counters behind list loading) must not
          // pull a trailing aggregate through.
          stillWanted: () => queryClient.getQueryCache().find({ queryKey })?.isActive() ?? false,
        },
      );
    },
    placeholderData: keepPreviousData,
    // Freshness paired with the email-index list query (30s + focus refetch):
    // the badge and the list must go stale together or perfect SQL parity
    // still renders badge≠list for minutes after any external change.
    // Accepted bound: cross-user/cron transitions appear on the next window
    // focus after 30s; without a focus event staleness is unbounded (no
    // polling) — same-user transitions all invalidate explicitly.
    staleTime: 30_000,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    // Paired with the list query's own `refetchOnMount: true`, so badge and list
    // refetch together on a reveal after 30s; it also covers a skipped trailing
    // fetch, which leaves the query invalidated on purpose and which the
    // app-wide default would never refetch on the next mount.
    refetchOnMount: true,
    retry: false,
  });

  useMembershipRecheck(error);

  // Use useMemo to create stable fallback object reference
  // This prevents creating a new object on every render when counts is undefined
  const fallbackCounts = useMemo(
    (): EmailFilterCounts => ({
      all: 0,
      inbox: 0,
      sent: 0,
      archived: 0,
      scheduled: 0,
      snoozed: 0,
      unread: 0,
      assigned: 0,
      todo: 0,
      done: 0,
      drafts: 0,
      approvals: 0,
      bin: 0,
      teamAssignmentCounts: {},
      inboxCounts: { all: 0, unread: 0, assigned_to_me: 0 },
      assignedCounts: { all: 0, unread: 0, assigned_to_me: 0 },
      draftsCounts: { all: 0, unread: 0, assigned_to_me: 0 },
      approvalsCounts: { all: 0, unread: 0, assigned_to_me: 0 },
      doneCounts: { all: 0, unread: 0, assigned_to_me: 0 },
      teamCounts: {},
      statusCounts: {},
    }),
    [],
  );

  // Memoize the final return value to ensure stable reference
  // Even though TanStack Query memoizes `counts`, we want to ensure
  // the return value doesn't change unless the actual counts data changes
  return useMemo(() => counts || fallbackCounts, [counts, fallbackCounts]);
}
